import {
    createContext, Dispatch,
    FC,
    ReactNode, SetStateAction,
    useContext, useRef,
    useState,
} from "react";
import {Window, WindowActionsEvent} from "@progress/kendo-react-dialogs";
import {useTranslation} from "react-i18next";
import StartJobConfirmationPanel, {
    StartJobConfirmationPanelProps
} from "@pages/orders/preview/popups/StartJobConfirmationPanel";
import DelegateOrderPanel, {DelegateOrderPanelProps} from "@pages/orders/preview/popups/DelegateOrderPanel";
import UpdateProjectLeaderPanel from "@pages/orders/preview/popups/UpdateProjectLeaderPanel";
import UpdateConfirmedDateComponent from "@pages/orders/preview/popups/UpdateConfirmedDateComponent";
import AssignCoordinatorComponent from "@pages/orders/preview/popups/AssignCoordinatorComponent";
import AddDeliveryDetailsComponent from "@pages/orders/preview/popups/AddDeliveryDetailsComponent";
import MissingAttachmentsComponent from "@pages/orders/preview/popups/MissingAttachmentsComponent";
import {
    OrderAttachmentSummaryValidationModel, OrderEditSaveSummaryModel,
    OrderPreviewModelStateType, OrderRecipePreviewModel
} from "@pages/orders/order.models";
import {GUID, VivoPortal} from "../AppConstants";
import InkComponentEditComponent
    from "@components/page_components/administration/inkseries/InkComponentEditComponent";
import CompanyListForUserComponent from "@components/page_components/users/edit/CompanyListForUserComponent";
import PrepareOrderForCompanyPopup from "@pages/orders/edit/popups/PrepareOrderForCompanyPopup";
import {DropDownListItem, NameWithId, TimeReportingModel} from "../Models";
import TextAreaInputDialog from "./common/TextAreaInputDialog";
import OrderEditSaveConfirmationPanel from "@pages/orders/edit/popups/OrderEditSaveConfirmationPanel";
import EnumRedirectAfterSave = VivoPortal.EnumRedirectAfterSave;
import CalendarSelectionDialog from "@utils/popups/common/CalendarSelectionDialog";
import SendEmailPopup from "@pages/administration/mailings/SendEmailPopup";
import ImportComponentsPanel from "@pages/administration/components/popups/ImportComponentsPanel";
import {ImportComponentsResponse} from "@pages/administration/components/component.models";
import EditorInputDialog from "@utils/popups/common/EditorInputDialog";
import ActivityTypeEditComponent from "@pages/work_evaluation/edit/ActivityTypeEditComponent";
import {OperationTypeModel} from "@pages/work_evaluation/workevaluation.models";
import EditProductCodeListItemComponent from "@pages/product_codes/common/EditProductCodeListItemComponent";
import {
    BaseProductCodeListItemModel,
    EmeaProductCodeListItemModel,
    NaProductCodeListItemModel
} from "@pages/product_codes/productcode.models";
import OrderActivitiesUpdateComponent, {
    OrderActivitiesResponseModel
} from "@pages/orders/preview/popups/OrderActivitiesUpdateComponent";
import OrderRecipeEditPopup from "@pages/orders/preview/recipes/edit/OrderRecipeEditPopup";
import OrderRecipeLookupPanel from "@pages/orders/preview/recipes/lookup/OrderRecipeLookupPanel";
import RecipeDescriptionGeneratorPanel from "@pages/orders/preview/recipes/edit/description_generator/RecipeDescriptionGeneratorPanel";

// Create Context
export const VivoPopupContext = createContext<VivoPopupContextProps | null>(null);

// Custom Hook
export function useVivoPopupContext() {
    const context = useContext(VivoPopupContext);

    if (!context) {
        throw new Error(
            "useVivoPopupContext must be used within an VivoPopupContextProvider"
        );
    }

    return context;
}

// Provider Component
export const VivoPopupContextProvider: FC<{ children: ReactNode }> = ({children}) => {
    const {t} = useTranslation();
    const orderActivitiesUpdatePropsRef = useRef<{
        timeReportingModel: TimeReportingModel,
        warnIfNotFilled: boolean
    }>({warnIfNotFilled: false, timeReportingModel: null!});
    const missingAttachmentsPropsRef = useRef<{
        isLoading: boolean,
        model: OrderAttachmentSummaryValidationModel
    }>({isLoading: false, model: null!});
    const inkComponentEditComponentPropsRef = useRef<{
        componentGuid?: GUID
    }>({});
    const addDeliveryDetailsComponentPropsRef = useRef<{
        orderPreviewModel: OrderPreviewModelStateType
    }>({orderPreviewModel: null!});
    const assignCoordinatorPropsRef = useRef<{
        orderPreviewModel: OrderPreviewModelStateType
    }>({orderPreviewModel: null!});
    const startJobConfirmationPanelPropsRef = useRef<{
        orderPreviewModel: OrderPreviewModelStateType
    }>({orderPreviewModel: null!});
    const delegateOrderPanelPropsRef = useRef<{
        orderPreviewModel: OrderPreviewModelStateType
    }>({orderPreviewModel: null!});
    const updateProjectLeaderPanelPropsRef = useRef<{
        orderPreviewModel: OrderPreviewModelStateType
    }>({orderPreviewModel: null!});
    const updateConfirmedDateComponentPropsRef = useRef<{
        orderPreviewModel: OrderPreviewModelStateType
    }>({orderPreviewModel: null!});
    const companyListForUserComponentRef = useRef<{
        labId: number,
        divisionId: number
    }>({labId: null!, divisionId: null!});
    const textAreaInputDialogRef = useRef<{
        title: string;
        description?: string;
        maxLength?: number;
    }>({title: null!});
    const editorInputDialogRef = useRef<{
        title: string;
        description?: string;
        initialValue?: string;
        isRequired?: boolean;
    }>({title: null!});
    const calendarSelectionDialogRef = useRef<{
        description?: string;
    }>({});
    const orderEditSaveConfirmationPanelRef = useRef<{
        summaryModel: OrderEditSaveSummaryModel;
    }>({summaryModel: null!});
    const activityTypeEditComponentRef = useRef<{
        activityTypeId: number;
        isReadOnly?: boolean;
    }>({activityTypeId: null!, isReadOnly: false});
    const editProductCodeListItemComponentRef = useRef<{
        labelKey: string;
        listItem: (EmeaProductCodeListItemModel | NaProductCodeListItemModel)
    }>({labelKey: "", listItem: null!});
    const orderRecipeEditPopupRef = useRef<{
        OrderGuid: GUID;
        ColourStandardGuid: GUID;
        RecipeGuid?: GUID;
        CanAddComponents?: boolean;
        SeriesId?: number;
        SubstrateId?: number;
        ColourStandardName?: string;
        ColourType?: NameWithId | null;
        SapCode?: string;
        ReadOnly?: boolean;
        AssignmentGuid?: GUID | null;
    }>({OrderGuid: null!, ColourStandardGuid: null!});
    const recipeDescriptionGeneratorPopupRef = useRef<{
        OrderGuid: GUID;
        InitialInkSerieId?: number;
        InitialSubstrateId?: number;
        InitialColourStandardName?: string;
        InitialColourType?: NameWithId | null;
    }>({
        OrderGuid: null!,
    });
    const orderRecipeLookupPopupRef = useRef<{
        OrderGuid: GUID;
        OrderItemGuid: GUID;
        ColourStandardGuid: GUID;
        InitialInkSerie: DropDownListItem;
        InitialSubstrate: DropDownListItem;
        InitialColorCode: string;
        SubstrateOptions?: DropDownListItem[];
        AlreadyAttachedRecipeGuids?: GUID[];
    }>({
        OrderGuid: null!,
        OrderItemGuid: null!,
        ColourStandardGuid: null!,
        InitialInkSerie: null!,
        InitialSubstrate: null!,
        InitialColorCode: "",
        SubstrateOptions: undefined,
        AlreadyAttachedRecipeGuids: undefined,
    });

    // Manage visibility and callbacks for popups
    const [popupState, setPopupState] = useState<PopupState>({
        StartJob: null,
        DelegateOrder: null,
        UpdateProjectLeader: null,
        UpdateConfirmedDate: null,
        AssignCoordinator: null,
        AddDeliveryDetails: null,
        MissingAttachments: null,
        OrderActivitiesUpdate: null,
        InkComponentEditComponent: null,
        CompanyListForUserComponent: null,
        PrepareOrderForCompanyPopup: null,
        TextAreaInputDialog: null,
        EditorInputDialog: null,
        OrderEditSaveConfirmationPanel: null,
        CalendarSelectionDialog: null,
        SendEmailPopup: null,
        ImportComponentsPanel: null,
        ActivityTypeEditComponent: null,
        EditProductCodeListItemComponent: null,
        OrderRecipeEditPopup: null,
        OrderRecipeLookupPopup: null,
        RecipeDescriptionGeneratorPopup: null,
    });

    function setV<T extends keyof PopupState>(key: T, value: PopupState[T]) {
        setPopupState((prev) => ({
            ...prev,
            [key]: value,
        }));
    }

    // == UNIVERSAL HANDLERS
    function triggerStart<T extends keyof PopupState, T1>(key: T): Promise<T1> {
        return new Promise<T1>((resolve, reject) => {
            setV(key, {resolve, reject})
        });
    }

    function handleCompleted<T extends keyof PopupState, T1>(key: T, args?: T1) {
        if (popupState[key]) {
            popupState[key]?.resolve(args);
        }
        setV(key, null);
    }

    function handleCancelled<T extends keyof PopupState>(key: T) {
        if (popupState[key]) {
            popupState[key]?.reject(new Error("Cancelled"));
        }
        setV(key, null);
    }

    return (
        <VivoPopupContext.Provider
            value={{
                popups: {
                    startWork: {
                        start: (model: OrderPreviewModelStateType) => {
                            startJobConfirmationPanelPropsRef.current = {orderPreviewModel: model};
                            return triggerStart("StartJob");
                        }
                    },
                    delegateOrder: {
                        start: (model: OrderPreviewModelStateType) => {
                            delegateOrderPanelPropsRef.current = {orderPreviewModel: model};
                            return triggerStart("DelegateOrder");
                        }
                    },
                    updateProjectLeader: {
                        start: (model: OrderPreviewModelStateType) => {
                            updateProjectLeaderPanelPropsRef.current = {orderPreviewModel: model};
                            return triggerStart("UpdateProjectLeader");
                        }
                    },
                    updateConfirmedDate: {
                        start: (model: OrderPreviewModelStateType) => {
                            updateConfirmedDateComponentPropsRef.current = {orderPreviewModel: model};
                            return triggerStart("UpdateConfirmedDate");
                        }
                    },
                    assignCoordinator: {
                        start: (model: OrderPreviewModelStateType) => {
                            assignCoordinatorPropsRef.current = {orderPreviewModel: model};
                            return triggerStart("AssignCoordinator");
                        }
                    },
                    addDeliveryDetails: {
                        start: (model: OrderPreviewModelStateType) => {
                            addDeliveryDetailsComponentPropsRef.current = {orderPreviewModel: model};
                            return triggerStart("AddDeliveryDetails");
                        }
                    },
                    missingAttachments: {
                        start: (isLoading: boolean, model: OrderAttachmentSummaryValidationModel) => {
                            missingAttachmentsPropsRef.current = {isLoading, model};
                            return triggerStart("MissingAttachments")
                        }
                    },
                    orderActivitiesUpdate: {
                        start: (timeReportingModel: TimeReportingModel, warnIfNotFilled: boolean) => {
                            orderActivitiesUpdatePropsRef.current = {timeReportingModel, warnIfNotFilled}
                            return triggerStart("OrderActivitiesUpdate")
                        }
                    },
                    inkComponentEditComponent: {
                        start: (componentGuid?: GUID) => {
                            inkComponentEditComponentPropsRef.current = {
                                componentGuid: componentGuid,
                            }
                            return triggerStart("InkComponentEditComponent")
                        }
                    },
                    companyListForUserComponent: {
                        start: (labId: number, divisionId: number) => {
                            companyListForUserComponentRef.current = {
                                labId: labId,
                                divisionId: divisionId
                            }
                            return triggerStart("CompanyListForUserComponent")
                        }
                    },
                    prepareOrderForCompanyPopup: {
                        start: () => {
                            return triggerStart("PrepareOrderForCompanyPopup")
                        }
                    },
                    textAreaInputDialog: {
                        start: (title: string, description?: string, maxLength?: number) => {
                            textAreaInputDialogRef.current = {title: title, description: description, maxLength: maxLength}
                            return triggerStart("TextAreaInputDialog")
                        }
                    },
                    editorInputDialog: {
                        start: (title: string, description?: string, initialValue?: string, isRequired?: boolean) => {
                            editorInputDialogRef.current = {
                                title: title,
                                description: description,
                                initialValue: initialValue,
                                isRequired: isRequired
                            }
                            return triggerStart("EditorInputDialog")
                        }
                    },
                    orderEditSaveConfirmationPanel: {
                        start: (summaryModel: OrderEditSaveSummaryModel) => {
                            orderEditSaveConfirmationPanelRef.current = {summaryModel: summaryModel}
                            return triggerStart("OrderEditSaveConfirmationPanel")
                        }
                    },
                    calendarSelectionDialog: {
                        start: (description?: string) => {
                            calendarSelectionDialogRef.current = {description: description}
                            return triggerStart("CalendarSelectionDialog")
                        }
                    },
                    sendEmailPopup: {
                        start: () => {
                            return triggerStart("SendEmailPopup")
                        }
                    },
                    importComponentsPanel: {
                        start: () => {
                            return triggerStart("ImportComponentsPanel")
                        }
                    },
                    activityTypeEditComponent: {
                        start: (activityTypeId: number, isReadOnly?: boolean) => {
                            activityTypeEditComponentRef.current = {
                                activityTypeId: activityTypeId,
                                isReadOnly: isReadOnly
                            }
                            return triggerStart("ActivityTypeEditComponent")
                        }
                    },
                    editProductCodeListItemComponent: {
                        start: (labelKey: string, listItem: (EmeaProductCodeListItemModel | NaProductCodeListItemModel)) => {
                            editProductCodeListItemComponentRef.current = {
                                labelKey: labelKey,
                                listItem: listItem
                            }
                            return triggerStart("EditProductCodeListItemComponent")
                        }
                    },
                    orderRecipeEditPopup: {
                        start: (orderGuid: GUID, colourStandardGuid: GUID, recipeGuid?: GUID, canAddComponents?: boolean, seriesId?: number, substrateId?: number, colourStandardName?: string, colourType?: NameWithId | null, sapCode?: string, readOnly?: boolean, assignmentGuid?: GUID | null) => {
                            orderRecipeEditPopupRef.current = {
                                OrderGuid: orderGuid,
                                ColourStandardGuid: colourStandardGuid,
                                RecipeGuid: recipeGuid,
                                CanAddComponents: canAddComponents,
                                SeriesId: seriesId,
                                SubstrateId: substrateId,
                                ColourStandardName: colourStandardName,
                                ColourType: colourType,
                                SapCode: sapCode,
                                ReadOnly: readOnly,
                                AssignmentGuid: assignmentGuid,
                            }
                            return triggerStart("OrderRecipeEditPopup")
                        }
                    },
                    recipeDescriptionGeneratorPopup: {
                        start: (initial: {
                            OrderGuid: GUID,
                            InitialInkSerieId?: number,
                            InitialSubstrateId?: number,
                            InitialColourStandardName?: string,
                            InitialColourType?: NameWithId | null,
                        }) => {
                            recipeDescriptionGeneratorPopupRef.current = {
                                OrderGuid: initial.OrderGuid,
                                InitialInkSerieId: initial.InitialInkSerieId,
                                InitialSubstrateId: initial.InitialSubstrateId,
                                InitialColourStandardName: initial.InitialColourStandardName,
                                InitialColourType: initial.InitialColourType,
                            };
                            return triggerStart("RecipeDescriptionGeneratorPopup");
                        }
                    },
                    orderRecipeLookupPopup: {
                        start: (orderGuid: GUID, orderItemGuid: GUID, colourStandardGuid: GUID, initial: {
                            InkSerie: DropDownListItem,
                            Substrate: DropDownListItem,
                            ColorCode: string,
                            SubstrateOptions?: DropDownListItem[]
                        }, alreadyAttachedRecipeGuids?: GUID[]) => {
                            orderRecipeLookupPopupRef.current = {
                                OrderGuid: orderGuid,
                                OrderItemGuid: orderItemGuid,
                                ColourStandardGuid: colourStandardGuid,
                                InitialInkSerie: initial.InkSerie,
                                InitialSubstrate: initial.Substrate,
                                InitialColorCode: initial.ColorCode,
                                SubstrateOptions: initial.SubstrateOptions,
                                AlreadyAttachedRecipeGuids: alreadyAttachedRecipeGuids,
                            }
                            return triggerStart("OrderRecipeLookupPopup")
                        }
                    },
                },
            }}>
            {children}
            {popupState.StartJob && (
                <Window
                    width={450}
                    height={550}
                    title={t("ORDER_START_PROCESSING")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("StartJob")}
                >
                    <StartJobConfirmationPanel
                        orderPreviewModelState={startJobConfirmationPanelPropsRef.current.orderPreviewModel}
                        OnCancelled={() => handleCancelled("StartJob")}
                        OnConfirmedAndSaved={() => handleCompleted("StartJob")}
                    />
                </Window>
            )}
            {popupState.DelegateOrder && (
                <Window
                    width={450}
                    height={450}
                    title={t("ORDER_DELEGATE_ORDER")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("DelegateOrder")}
                >
                    <DelegateOrderPanel
                        orderPreviewModelState={delegateOrderPanelPropsRef.current.orderPreviewModel}
                        OnCancelled={() => handleCancelled("DelegateOrder")}
                        OnConfirmedAndSaved={() => handleCompleted("DelegateOrder")}
                    />
                </Window>
            )}
            {popupState.UpdateProjectLeader && (
                <Window
                    width={450}
                    height={250}
                    title={t("ORDER_PREVIEW_UpdateProjectLeaderTitle")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("UpdateProjectLeader")}
                >
                    <UpdateProjectLeaderPanel
                        orderPreviewModelState={updateProjectLeaderPanelPropsRef.current.orderPreviewModel}
                        OnCancelled={() => handleCancelled("UpdateProjectLeader")}
                        OnConfirmedAndSaved={() => handleCompleted("UpdateProjectLeader")}
                    />
                </Window>
            )}
            {popupState.UpdateConfirmedDate && (
                <Window
                    width={450}
                    height={450}
                    title={t("ORDER_PREVIEW_ConfirmedDateUpdatePopupTitle")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("UpdateConfirmedDate")}
                >
                    <UpdateConfirmedDateComponent
                        orderPreviewModelState={updateConfirmedDateComponentPropsRef.current.orderPreviewModel}
                        OnCancelled={() => handleCancelled("UpdateConfirmedDate")}
                        OnConfirmedAndSaved={() => handleCompleted("UpdateConfirmedDate")}
                    />
                </Window>
            )}
            {popupState.AssignCoordinator && (
                <Window
                    width={450}
                    height={250}
                    title={t("ORDER_PREVIEW_PROJECT_SelectCoordinator")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("AssignCoordinator")}
                >
                    <AssignCoordinatorComponent
                        orderPreviewModelState={assignCoordinatorPropsRef.current.orderPreviewModel}
                        OnCancelled={() => handleCancelled("AssignCoordinator")}
                        OnConfirmedAndSaved={() => handleCompleted("AssignCoordinator")}
                    />
                </Window>
            )}
            {popupState.AddDeliveryDetails && (
                <Window
                    width={600}
                    height={450}
                    title={t("ORDER_PREVIEW_TRACKINGNUMBER_PopupTitle")}
                    modal={true}
                    //closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("AddDeliveryDetails")}
                >
                    <AddDeliveryDetailsComponent
                        orderPreviewModelState={addDeliveryDetailsComponentPropsRef.current.orderPreviewModel}
                        OnCancelled={() => handleCancelled("AddDeliveryDetails")}
                        OnConfirmedAndSaved={() => handleCompleted("AddDeliveryDetails")}
                    />
                </Window>
            )}
            {popupState.MissingAttachments && (
                <Window
                    width={550}
                    height={450}
                    title={t("ORDER_PREVIEW_MissingAttachmentsTitle")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("MissingAttachments")}
                >
                    <MissingAttachmentsComponent
                        isLoading={missingAttachmentsPropsRef.current.isLoading}
                        model={missingAttachmentsPropsRef.current.model!}
                        OnCancelled={() => handleCancelled("MissingAttachments")}
                        OnConfirmedAndSaved={() => handleCompleted("MissingAttachments")}
                    />
                </Window>
            )}

            {popupState.OrderActivitiesUpdate && (
                <Window
                    width={800}
                    height={550}
                    title={t("COMMON_FillTheForm")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("OrderActivitiesUpdate")}
                >
                    <OrderActivitiesUpdateComponent
                        TimeReportingModel={orderActivitiesUpdatePropsRef.current.timeReportingModel}
                        WarnIfNotFilled={orderActivitiesUpdatePropsRef.current.warnIfNotFilled}
                        OnCancelled={() => handleCancelled("OrderActivitiesUpdate")}
                        OnConfirmedAndSaved={(response) =>
                            handleCompleted("OrderActivitiesUpdate", response)}
                    />
                </Window>
            )}
            {popupState.InkComponentEditComponent && (
                <Window
                    width={550}
                    height={550}
                    title={inkComponentEditComponentPropsRef.current.componentGuid ? t("COMPONENTS_Edit") : t("COMPONENTS_AddNew")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("InkComponentEditComponent")}
                >
                    <InkComponentEditComponent
                        componentGuid={inkComponentEditComponentPropsRef.current.componentGuid}
                        OnCancelled={() => handleCancelled("InkComponentEditComponent")}
                        OnConfirmedAndSaved={() => handleCompleted("InkComponentEditComponent")}
                    />
                </Window>
            )}
            {popupState.CompanyListForUserComponent && (
                <Window
                    width={900}
                    height={650}
                    title={t("USER_FILTER_Customers")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("CompanyListForUserComponent")}
                >
                    <CompanyListForUserComponent
                        LabId={companyListForUserComponentRef.current.labId}
                        DivisionId={companyListForUserComponentRef.current.divisionId}
                        OnCancelled={() => handleCancelled("CompanyListForUserComponent")}
                        OnConfirmedAndSaved={(company) => handleCompleted("CompanyListForUserComponent",
                            company)}
                    />
                </Window>
            )}
            {popupState.PrepareOrderForCompanyPopup && (
                <Window
                    width={600}
                    height={350}
                    title={t("ORDER_PREPARE_FOR_COMPANY")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("PrepareOrderForCompanyPopup")}
                >
                    <PrepareOrderForCompanyPopup
                        OnCancelled={() => handleCancelled("PrepareOrderForCompanyPopup")}
                        OnConfirmedAndSaved={(company: NameWithId | void) =>
                            handleCompleted<"PrepareOrderForCompanyPopup", NameWithId | void>("PrepareOrderForCompanyPopup", company)}
                    />
                </Window>
            )}
            {popupState.TextAreaInputDialog && (
                <Window
                    width={600}
                    height={350}
                    title={textAreaInputDialogRef.current.title}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("TextAreaInputDialog")}
                >
                    <TextAreaInputDialog description={textAreaInputDialogRef.current.description}
                                         maxLength={textAreaInputDialogRef.current.maxLength}
                                         OnCancelled={() => handleCancelled("TextAreaInputDialog")}
                                         OnConfirmedAndSaved={(message: string | void) =>
                                             handleCompleted<"TextAreaInputDialog", string | void>("TextAreaInputDialog", message)}
                    />
                </Window>
            )}
            {popupState.EditorInputDialog && (
                <Window
                    width={800}
                    height={550}
                    title={editorInputDialogRef.current.title}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("EditorInputDialog")}
                >
                    <EditorInputDialog description={editorInputDialogRef.current.description}
                                       Required={editorInputDialogRef.current.isRequired}
                                       InitialValue={editorInputDialogRef.current.initialValue}
                                       OnCancelled={() => handleCancelled("EditorInputDialog")}
                                       OnConfirmedAndSaved={(message: string | void) =>
                                           handleCompleted<"EditorInputDialog", string | void>("EditorInputDialog", message)}
                    />
                </Window>
            )}

            {popupState.OrderEditSaveConfirmationPanel && (
                <Window
                    width={800}
                    height={670}
                    title={t("ORDER_EDIT_SaveConfirmationTitle")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("OrderEditSaveConfirmationPanel")}
                >
                    <OrderEditSaveConfirmationPanel
                        saveOrderSummary={orderEditSaveConfirmationPanelRef.current.summaryModel}
                        OnCancelled={() => handleCancelled("OrderEditSaveConfirmationPanel")}
                        OnConfirmedAndSaved={(redirectType: EnumRedirectAfterSave | void) =>
                            handleCompleted<"OrderEditSaveConfirmationPanel", EnumRedirectAfterSave | void>("OrderEditSaveConfirmationPanel", redirectType)}
                    />
                </Window>
            )}
            {popupState.CalendarSelectionDialog && (
                <Window
                    style={{maxWidth: "100%", maxHeight:"100%"}}
                    width={500}
                    height={600}
                    title={t("COMMON_SelectDate")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("CalendarSelectionDialog")}
                >
                    <CalendarSelectionDialog description={calendarSelectionDialogRef.current.description}
                                             OnCancelled={() => handleCancelled("CalendarSelectionDialog")}
                                             OnConfirmedAndSaved={(selectedDate: Date | void) =>
                                                 handleCompleted<"CalendarSelectionDialog", Date | void>("CalendarSelectionDialog", selectedDate)}
                    />
                </Window>
            )}

            {popupState.SendEmailPopup && (
                <Window
                    style={{maxWidth: "100%", maxHeight:"100%"}}
                    width={800}
                    height={650}
                    title={"Quick component import"}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("SendEmailPopup")}
                >
                    <SendEmailPopup OnCancelled={() => handleCancelled("SendEmailPopup")}
                                    OnConfirmedAndSaved={(mailing: NameWithId | void) =>
                                        handleCompleted<"SendEmailPopup", NameWithId | void>("SendEmailPopup", mailing)}/>
                </Window>
            )}
            {popupState.ImportComponentsPanel && (
                <Window
                    style={{maxWidth: "100%", maxHeight:"100%"}}
                    width={700}
                    height={650}
                    title={t("LAB_ImportComponents")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("ImportComponentsPanel")}
                >
                    <ImportComponentsPanel OnCancelled={() => handleCancelled("ImportComponentsPanel")}
                                           OnConfirmedAndSaved={(response: ImportComponentsResponse | void) =>
                                               handleCompleted("ImportComponentsPanel", response)}/>
                </Window>
            )}
            {popupState.ActivityTypeEditComponent && (
                <Window style={{maxWidth: "100%", maxHeight:"100%"}}
                        width={500}
                        height={500}
                        title={t("lblOperationType")}
                        modal={true}
                    // closeButton={() => <></>}
                        onClose={(args: WindowActionsEvent) => handleCancelled("ActivityTypeEditComponent")}
                >
                    <ActivityTypeEditComponent
                        activityTypeId={activityTypeEditComponentRef.current.activityTypeId}
                        isReadOnly={activityTypeEditComponentRef.current.isReadOnly}
                        OnCancelled={() => handleCancelled("ActivityTypeEditComponent")}
                        OnConfirmedAndSaved={(response: OperationTypeModel | void) => handleCompleted("ActivityTypeEditComponent", response)}
                    />
                </Window>
            )}
            {popupState.EditProductCodeListItemComponent && (
                <Window style={{maxWidth: "100%", maxHeight:"100%"}}
                        width={500}
                        height={500}
                        title={`${t("edit")}: ${t(editProductCodeListItemComponentRef.current.labelKey)}`}
                        modal={true}
                    // closeButton={() => <></>}
                        onClose={(args: WindowActionsEvent) => handleCancelled("EditProductCodeListItemComponent")}
                >
                    <EditProductCodeListItemComponent
                        labelKey={editProductCodeListItemComponentRef.current.labelKey}
                        listItem={editProductCodeListItemComponentRef.current.listItem}
                        OnCancelled={() => handleCancelled("EditProductCodeListItemComponent")}
                        OnConfirmedAndSaved={(response: (EmeaProductCodeListItemModel | NaProductCodeListItemModel) | void) =>
                            handleCompleted<"EditProductCodeListItemComponent", (EmeaProductCodeListItemModel | NaProductCodeListItemModel) | void>("EditProductCodeListItemComponent", response)}
                    />
                </Window>
            )}
            {popupState.OrderRecipeEditPopup && (
                <Window style={{maxWidth: "100%", maxHeight:"100%"}}
                        width={1400}
                        height={800}
                        title={orderRecipeEditPopupRef.current.RecipeGuid
                            ? t("ORDER_PREVIEW_RecipeEditWindowTitle")
                            : t("ORDER_PREVIEW_RecipeNewWindowTitle")}
                        modal={true}
                    // closeButton={() => <></>}
                        onClose={(args: WindowActionsEvent) => handleCancelled("OrderRecipeEditPopup")}
                >
                    <OrderRecipeEditPopup
                        OrderGuid={orderRecipeEditPopupRef.current.OrderGuid}
                        ColourStandardGuid={orderRecipeEditPopupRef.current.ColourStandardGuid}
                        RecipeGuid={orderRecipeEditPopupRef.current.RecipeGuid}
                        CanAddComponents={orderRecipeEditPopupRef.current.CanAddComponents}
                        SeriesId={orderRecipeEditPopupRef.current.SeriesId}
                        SubstrateId={orderRecipeEditPopupRef.current.SubstrateId}
                        ColourStandardName={orderRecipeEditPopupRef.current.ColourStandardName}
                        ColourType={orderRecipeEditPopupRef.current.ColourType}
                        SapCodeOverride={orderRecipeEditPopupRef.current.SapCode}
                        ReadOnly={orderRecipeEditPopupRef.current.ReadOnly}
                        AssignmentGuid={orderRecipeEditPopupRef.current.AssignmentGuid}
                        OnCancelled={() => handleCancelled("OrderRecipeEditPopup")}
                        OnConfirmedAndSaved={(response: OrderRecipePreviewModel | void) =>
                            handleCompleted<"OrderRecipeEditPopup", OrderRecipePreviewModel | void>("OrderRecipeEditPopup", response)}
                    />
                </Window>
            )}
            {popupState.RecipeDescriptionGeneratorPopup && (
                <Window style={{maxWidth: "100%", maxHeight: "100%"}}
                        width={560}
                        height={520}
                        title={t("RECIPES_DescriptionGenerator_Title")}
                        modal={true}
                        onClose={(args: WindowActionsEvent) => handleCancelled("RecipeDescriptionGeneratorPopup")}
                >
                    <RecipeDescriptionGeneratorPanel
                        OrderGuid={recipeDescriptionGeneratorPopupRef.current.OrderGuid}
                        InitialInkSerieId={recipeDescriptionGeneratorPopupRef.current.InitialInkSerieId}
                        InitialSubstrateId={recipeDescriptionGeneratorPopupRef.current.InitialSubstrateId}
                        InitialColourStandardName={recipeDescriptionGeneratorPopupRef.current.InitialColourStandardName}
                        InitialColourType={recipeDescriptionGeneratorPopupRef.current.InitialColourType}
                        OnAccepted={(value: string) => handleCompleted<"RecipeDescriptionGeneratorPopup", string>("RecipeDescriptionGeneratorPopup", value)}
                        OnCancelled={() => handleCancelled("RecipeDescriptionGeneratorPopup")}
                    />
                </Window>
            )}
            {popupState.OrderRecipeLookupPopup && (
                <Window style={{maxWidth: "100%", maxHeight:"100%"}}
                        width={1100}
                        height={700}
                        title={t("ORDER_PREVIEW_RecipeLookupTitle")}
                        modal={true}
                        onClose={(args: WindowActionsEvent) => handleCancelled("OrderRecipeLookupPopup")}
                >
                    <OrderRecipeLookupPanel
                        OrderGuid={orderRecipeLookupPopupRef.current.OrderGuid}
                        OrderItemGuid={orderRecipeLookupPopupRef.current.OrderItemGuid}
                        ColourStandardGuid={orderRecipeLookupPopupRef.current.ColourStandardGuid}
                        InitialInkSerie={orderRecipeLookupPopupRef.current.InitialInkSerie}
                        InitialSubstrate={orderRecipeLookupPopupRef.current.InitialSubstrate}
                        InitialColorCode={orderRecipeLookupPopupRef.current.InitialColorCode}
                        SubstrateOptions={orderRecipeLookupPopupRef.current.SubstrateOptions}
                        AlreadyAttachedRecipeGuids={orderRecipeLookupPopupRef.current.AlreadyAttachedRecipeGuids}
                        OnAttached={(recipe: OrderRecipePreviewModel) =>
                            handleCompleted<"OrderRecipeLookupPopup", OrderRecipePreviewModel>("OrderRecipeLookupPopup", recipe)}
                        OnClosed={() => handleCancelled("OrderRecipeLookupPopup")}
                    />
                </Window>
            )}
        </VivoPopupContext.Provider>
    );
};

// Context Props
interface VivoPopupContextProps {
    popups: {
        startWork: {
            start: (model: OrderPreviewModelStateType) => Promise<void>;
        };
        delegateOrder: {
            start: (model: OrderPreviewModelStateType) => Promise<void>;
        };
        updateProjectLeader: {
            start: (model: OrderPreviewModelStateType) => Promise<void>;
        };
        updateConfirmedDate: {
            start: (model: OrderPreviewModelStateType) => Promise<void>;
        };
        assignCoordinator: {
            start: (orderPreviewModelState: OrderPreviewModelStateType) => Promise<void>;
        };
        addDeliveryDetails: {
            start: (orderPreviewModelState: OrderPreviewModelStateType) => Promise<void>;
        };
        missingAttachments: {
            start: (isLoading: boolean, model: OrderAttachmentSummaryValidationModel) => Promise<void>;
        };
        orderActivitiesUpdate: {
            start: (timeReportingModel: TimeReportingModel, warnIfNotFilled: boolean) => Promise<OrderActivitiesResponseModel>;
        };
        inkComponentEditComponent: {
            start: (componentGuid?: GUID) => Promise<void>;
        };
        companyListForUserComponent: {
            start: (labId: number, divisionId: number) => Promise<void>;
        };
        prepareOrderForCompanyPopup: {
            start: () => Promise<NameWithId>;
        };
        textAreaInputDialog: {
            start: (title: string, description?: string, maxLength?: number) => Promise<string>;
        };
        editorInputDialog: {
            start: (title: string, description?: string, initialValue?: string, isRequired?: boolean) => Promise<string>;
        };
        orderEditSaveConfirmationPanel: {
            start: (summaryModel: OrderEditSaveSummaryModel) => Promise<EnumRedirectAfterSave>;
        };
        calendarSelectionDialog: {
            start: (description?: string) => Promise<Date>;
        };
        sendEmailPopup: {
            start: () => Promise<NameWithId>;
        };
        importComponentsPanel: {
            start: () => Promise<ImportComponentsResponse>;
        };
        activityTypeEditComponent: {
            start: (activityTypeId: number, isReadOnly?: boolean) => Promise<OperationTypeModel>;
        };
        editProductCodeListItemComponent: {
            start: (labelKey: string, listItem: EmeaProductCodeListItemModel | NaProductCodeListItemModel) => Promise<(EmeaProductCodeListItemModel | NaProductCodeListItemModel)>;
        };
        orderRecipeEditPopup: {
            start: (OrderGuid: GUID, ColourStandardGuid: GUID, RecipeGuid?: GUID, CanAddComponents?: boolean, SeriesId?: number, SubstrateId?: number, ColourStandardName?: string, ColourType?: NameWithId | null, SapCode?: string, ReadOnly?: boolean, AssignmentGuid?: GUID | null) => Promise<OrderRecipePreviewModel>;
        };
        recipeDescriptionGeneratorPopup: {
            start: (initial: {
                OrderGuid: GUID,
                InitialInkSerieId?: number,
                InitialSubstrateId?: number,
                InitialColourStandardName?: string,
                InitialColourType?: NameWithId | null,
            }) => Promise<string>;
        };
        orderRecipeLookupPopup: {
            start: (OrderGuid: GUID, OrderItemGuid: GUID, ColourStandardGuid: GUID, initial: {
                InkSerie: DropDownListItem,
                Substrate: DropDownListItem,
                ColorCode: string,
                SubstrateOptions?: DropDownListItem[]
            }, AlreadyAttachedRecipeGuids?: GUID[]) => Promise<OrderRecipePreviewModel>;
        };
    };
}

interface PopupState {
    StartJob: PopupCallbacks | null;
    DelegateOrder: PopupCallbacks | null;
    UpdateProjectLeader: PopupCallbacks | null;
    UpdateConfirmedDate: PopupCallbacks | null;
    AssignCoordinator: PopupCallbacks | null;
    AddDeliveryDetails: PopupCallbacks | null;
    MissingAttachments: PopupCallbacks | null;
    OrderActivitiesUpdate: PopupCallbacks | null;
    InkComponentEditComponent: PopupCallbacks | null;
    CompanyListForUserComponent: PopupCallbacks | null;
    PrepareOrderForCompanyPopup: PopupCallbacks | null;
    TextAreaInputDialog: PopupCallbacks | null;
    EditorInputDialog: PopupCallbacks | null;
    OrderEditSaveConfirmationPanel: PopupCallbacks | null;
    CalendarSelectionDialog: PopupCallbacks | null;
    SendEmailPopup: PopupCallbacks | null;
    ImportComponentsPanel: PopupCallbacks | null;
    ActivityTypeEditComponent: PopupCallbacks | null;
    EditProductCodeListItemComponent: PopupCallbacks | null;
    OrderRecipeEditPopup: PopupCallbacks | null;
    OrderRecipeLookupPopup: PopupCallbacks | null;
    RecipeDescriptionGeneratorPopup: PopupCallbacks | null;
}

interface PopupCallbacks<T = null> {
    resolve: (args: any) => void;
    reject: (reason?: any) => void;
}
